import { useState, useEffect, useCallback } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { UserCog, Plus, Pencil, Trash2, Shield, KeyRound } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import ClerkStatusList from '../../components/ClerkStatusList';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_LABELS = [
  // Top-level pages
  { key: 'can_view_dashboard', label: 'Dashboard', group: 'Pages' },
  { key: 'can_view_group_overview', label: 'JSM Group Overview', group: 'Pages' },
  { key: 'can_view_tenders', label: 'Live Tenders', group: 'Pages' },
  { key: 'can_manage_drivers', label: 'Owner Drivers', group: 'Pages' },
  { key: 'can_view_credentials', label: 'Credentials', group: 'Pages' },
  { key: 'can_manage_employees', label: 'Employees', group: 'Pages' },
  { key: 'can_manage_clerks', label: 'Transport Clerks (manage)', group: 'Pages' },
  { key: 'can_view_admin_permissions', label: 'Admin Permissions', group: 'Pages' },
  { key: 'can_manage_registrations', label: 'Registrations', group: 'Pages' },
  { key: 'can_view_statements', label: 'Statements', group: 'Pages' },
  // Finance
  { key: 'can_view_transport_pnl', label: 'Transport P&L', group: 'Finance' },
  { key: 'can_manage_transport_revenue', label: 'Transport Revenue', group: 'Finance' },
  { key: 'can_manage_transport_expenses', label: 'Transport Expenses', group: 'Finance' },
  { key: 'can_manage_loans', label: '2K2Day Loans', group: 'Finance' },
  // Banking
  { key: 'can_manage_banking', label: 'Banking', group: 'Banking' },
  { key: 'can_manage_exports', label: 'Exports', group: 'Banking' },
  // Other modules
  { key: 'can_manage_fuel_tracker', label: 'Fuel Tracker', group: 'Other' },
  { key: 'can_view_audit_logs', label: 'Audit Logs', group: 'Other' },
  { key: 'can_manage_backups', label: 'Backups & Recovery', group: 'Other' },
  { key: 'can_view_auto_spares', label: 'Auto Spares Sales', group: 'Other' },
  { key: 'can_ring_up_spares_sales', label: 'Auto Spares — Sales Ring-Up (full ADD SALE form, last 3 days view)', group: 'Other' },
  { key: 'can_manage_loans_tracker', label: '2K2Day Loan Tracker', group: 'Other' },
  { key: 'can_vet_loans', label: '2K2Day Vetting Engine', group: 'Other' },
];

const ALL_PERM_KEYS = PERMISSION_LABELS.map(p => p.key);
const buildEmptyPerms = () => ALL_PERM_KEYS.reduce((acc, k) => ({ ...acc, [k]: false }), {});

const emptyForm = () => ({
  username: '',
  full_name: '',
  password: '',
  permissions: buildEmptyPerms(),
});

export default function Clerks() {
  const [clerks, setClerks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [form, setForm] = useState(emptyForm());
  const [editing, setEditing] = useState(null);

  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchClerks = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/clerks`, { headers });
      setClerks(res.data);
    } catch {
      toast.error('Failed to load clerks');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchClerks(); }, [fetchClerks]);

  const handleAdd = async () => {
    if (!form.username.trim() || !form.full_name.trim() || !form.password) {
      toast.error('Username, name and password required');
      return;
    }
    if (form.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await axios.post(`${API_URL}/api/clerks`, form, { headers });
      toast.success('Clerk created!');
      setShowAdd(false);
      setForm(emptyForm());
      fetchClerks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create clerk');
    }
  };

  const handleEdit = async () => {
    if (!editing) return;
    try {
      const payload = {
        full_name: editing.full_name,
        permissions: editing.permissions,
        is_active: editing.is_active,
      };
      if (editing._newPassword) payload.password = editing._newPassword;
      await axios.put(`${API_URL}/api/clerks/${editing.id}`, payload, { headers });
      toast.success('Clerk updated!');
      setShowEdit(false);
      setEditing(null);
      fetchClerks();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    }
  };

  const handleDelete = async (c) => {
    if (!window.confirm(`Delete clerk "${c.full_name}"?`)) return;
    try {
      await axios.delete(`${API_URL}/api/clerks/${c.id}`, { headers });
      toast.success('Clerk deleted');
      fetchClerks();
    } catch {
      toast.error('Failed');
    }
  };

  const openEdit = (c) => {
    setEditing({
      ...c,
      permissions: { ...(c.permissions || {}) },
      _newPassword: '',
    });
    setShowEdit(true);
  };

  const countActivePerms = (perms) =>
    Object.values(perms || {}).filter(Boolean).length;

  if (loading) {
    return <DashboardLayout><div className="flex items-center justify-center h-64">Loading...</div></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <div className="space-y-5">
        <ClerkStatusList />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <UserCog className="h-6 w-6 text-indigo-500" /> Transport Clerks
            </h1>
            <p className="text-slate-500 text-sm">Create & manage clerk users with fine-grained permissions for Transport and Finance pages.</p>
          </div>
          <Dialog open={showAdd} onOpenChange={(o) => { setShowAdd(o); if (!o) setForm(emptyForm()); }}>
            <DialogTrigger asChild>
              <Button className="bg-indigo-600 hover:bg-indigo-700" data-testid="add-clerk-btn">
                <Plus className="h-4 w-4 mr-1" /> Add Clerk
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl max-h-[95vh] overflow-y-auto">
              <DialogHeader><DialogTitle>Create New Clerk</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <Label>Username *</Label>
                  <Input value={form.username} onChange={e => setForm({ ...form, username: e.target.value })} placeholder="e.g., jsmith" data-testid="clerk-username" />
                </div>
                <div>
                  <Label>Full Name *</Label>
                  <Input value={form.full_name} onChange={e => setForm({ ...form, full_name: e.target.value })} placeholder="e.g., John Smith" data-testid="clerk-fullname" />
                </div>
                <div>
                  <Label>Password * (min 6 chars)</Label>
                  <Input type="text" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })} data-testid="clerk-password" />
                </div>
                <div className="border-t pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-indigo-500" />
                    <span className="font-semibold text-sm">Permissions</span>
                  </div>
                  {PERMISSION_LABELS.map(p => (
                    <div key={p.key} className="flex items-center justify-between py-1.5">
                      <Label htmlFor={`new-${p.key}`} className="text-sm cursor-pointer flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 w-16 justify-center">
                          {p.group}
                        </Badge>
                        {p.label}
                      </Label>
                      <Switch
                        id={`new-${p.key}`}
                        checked={form.permissions[p.key]}
                        onCheckedChange={v => setForm({ ...form, permissions: { ...form.permissions, [p.key]: v } })}
                        data-testid={`perm-${p.key}`}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowAdd(false)}>Cancel</Button>
                <Button onClick={handleAdd} className="bg-indigo-600 hover:bg-indigo-700" data-testid="save-clerk-btn">Create Clerk</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        <Card>
          <CardHeader><CardTitle className="text-base">All Clerks ({clerks.length})</CardTitle></CardHeader>
          <CardContent className="p-0">
            {clerks.length === 0 ? (
              <div className="p-10 text-center text-slate-400">
                <UserCog className="h-12 w-12 mx-auto mb-3 opacity-40" />
                <p>No clerks yet. Add one to grant restricted Transport/Finance access.</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Username</TableHead>
                    <TableHead>Full Name</TableHead>
                    <TableHead>Permissions</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {clerks.map(c => (
                    <TableRow key={c.id} data-testid={`clerk-row-${c.username}`}>
                      <TableCell className="font-mono text-sm">{c.username}</TableCell>
                      <TableCell className="font-medium">{c.full_name}</TableCell>
                      <TableCell>
                        <Badge variant="secondary">{countActivePerms(c.permissions)} / {PERMISSION_LABELS.length}</Badge>
                      </TableCell>
                      <TableCell>
                        {c.is_active ? <Badge className="bg-emerald-600">Active</Badge> : <Badge variant="destructive">Disabled</Badge>}
                      </TableCell>
                      <TableCell className="text-xs text-slate-500">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString() : '-'}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex gap-1 justify-end">
                          <Button size="sm" variant="outline" onClick={() => openEdit(c)} data-testid={`edit-${c.username}`}>
                            <Pencil className="h-3 w-3" />
                          </Button>
                          <Button size="sm" variant="destructive" onClick={() => handleDelete(c)} data-testid={`delete-${c.username}`}>
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Dialog open={showEdit} onOpenChange={setShowEdit}>
          <DialogContent className="max-w-xl max-h-[95vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Edit Clerk — {editing?.username}</DialogTitle></DialogHeader>
            {editing && (
              <div className="space-y-3">
                <div>
                  <Label>Full Name</Label>
                  <Input value={editing.full_name} onChange={e => setEditing({ ...editing, full_name: e.target.value })} />
                </div>
                <div>
                  <Label className="flex items-center gap-1"><KeyRound className="h-3 w-3" /> Reset Password (leave blank to keep)</Label>
                  <Input type="text" value={editing._newPassword} onChange={e => setEditing({ ...editing, _newPassword: e.target.value })} placeholder="New password" />
                </div>
                <div className="flex items-center justify-between border-t pt-3">
                  <Label htmlFor="active-switch">Account Active</Label>
                  <Switch id="active-switch" checked={editing.is_active} onCheckedChange={v => setEditing({ ...editing, is_active: v })} />
                </div>
                <div className="border-t pt-3">
                  <div className="flex items-center gap-2 mb-2">
                    <Shield className="h-4 w-4 text-indigo-500" />
                    <span className="font-semibold text-sm">Permissions</span>
                  </div>
                  {PERMISSION_LABELS.map(p => (
                    <div key={p.key} className="flex items-center justify-between py-1.5">
                      <Label htmlFor={`edit-${p.key}`} className="text-sm cursor-pointer flex items-center gap-2">
                        <Badge variant="outline" className="text-[10px] px-1.5 py-0 w-16 justify-center">{p.group}</Badge>
                        {p.label}
                      </Label>
                      <Switch
                        id={`edit-${p.key}`}
                        checked={!!editing.permissions[p.key]}
                        onCheckedChange={v => setEditing({ ...editing, permissions: { ...editing.permissions, [p.key]: v } })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowEdit(false)}>Cancel</Button>
              <Button onClick={handleEdit} className="bg-indigo-600 hover:bg-indigo-700">Save Changes</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
